import { useEffect, useMemo, useState } from "react";
import { api } from "../utils/api";

type ProgressEntry = {
  id: string;
  date: string;
  weight: number | null;
  body_fat: number | null;
  measurements_json: string | null;
  photo_url: string | null;
};

type PersonalRecord = {
  id: string;
  sport_type: string;
  metric: string;
  value: number;
  unit: string;
  achieved_at: number;
};

type TLPoint = { date: string; ctl: number; atl: number; tsb: number };
type TLResponse = { data: TLPoint[]; summary: { current_ctl: number; current_tsb: number } };

const M = { chest: "", waist: "", hips: "", arms: "", thighs: "" };
const ML: Record<string, string> = {
  distance: "Distance", power_1s: "Power 1s", power_5s: "Power 5s",
  power_1min: "Power 1min", power_5min: "Power 5min", power_20min: "Power 20min",
  power_60min: "Power 60min", time_5k: "5K Time", time_10k: "10K Time",
  time_half_marathon: "Half Marathon", time_marathon: "Marathon",
};

const CW = 600, CH = 220, PL = 36, PR = 12, PT = 16, PB = 28;

function todayIso() { return new Date().toISOString().split("T")[0]; }
function fmtDate(d: string) { try { return new Date(d).toLocaleDateString(); } catch { return d; } }

function TrainingLoadChart({ data }: { data: TLPoint[] }) {
  if (data.length === 0) return <div style={s.chartEmpty}>No training data yet</div>;
  const iw = CW - PL - PR, ih = CH - PT - PB;
  let yMax = 0, yMin = 0;
  for (const d of data) { yMax = Math.max(yMax, d.ctl, d.atl, d.tsb); yMin = Math.min(yMin, d.tsb); }
  if (yMax === 0) yMax = 100;
  const yr = yMax - yMin, yp = yr === 0 ? Math.max(10, Math.abs(yMax) * 0.1) : yr * 0.1;
  const yM = yMax + yp, ym = yMin - yp, ys = yM - ym || 1;
  const xs = data.length > 1 ? iw / (data.length - 1) : 0;
  const tY = (v: number) => PT + ih - ((v - ym) / ys) * ih;
  const tX = (i: number) => PL + (data.length > 1 ? i * xs : iw / 2);
  const path = (k: "ctl" | "atl" | "tsb") =>
    data.map((d, i) => `${i ? "L" : "M"} ${tX(i).toFixed(1)} ${tY(d[k]).toFixed(1)}`).join(" ");
  const yt = Array.from({ length: 5 }, (_, i) => ({ v: ym + (ys * i) / 4, y: tY(ym + (ys * i) / 4) }));
  const step = Math.max(1, Math.floor(data.length / 5));
  const xt = Array.from({ length: Math.ceil(data.length / step) }, (_, i) => ({ i: i * step, x: tX(i * step) }));
  return (
    <div style={s.chartWrap}>
      <svg viewBox={`0 0 ${CW} ${CH}`} style={s.chartSvg} role="img" aria-label="Training load over time">
        {yt.map((t, i) => (
          <g key={i}>
            <line x1={PL} x2={CW - PR} y1={t.y} y2={t.y} stroke="#333" strokeWidth={1} />
            <text x={PL - 4} y={t.y + 3} textAnchor="end" fontSize={10} fill="#888">{Math.round(t.v)}</text>
          </g>
        ))}
        <line x1={PL} x2={CW - PR} y1={tY(0)} y2={tY(0)} stroke="#555" strokeWidth={1} />
        {xt.map((t) => (
          <text key={t.i} x={t.x} y={CH - 8} textAnchor="middle" fontSize={10} fill="#888">{data[t.i].date.slice(5)}</text>
        ))}
        <path d={path("ctl")} fill="none" stroke="#4aa3ff" strokeWidth={2} />
        <path d={path("atl")} fill="none" stroke="#7ed957" strokeWidth={2} />
        <path d={path("tsb")} fill="none" stroke="#ff6b6b" strokeWidth={2} />
      </svg>
      <div style={s.legend}>
        <span style={s.legendItem}><span style={{ ...s.swatch, backgroundColor: "#4aa3ff" }} />CTL</span>
        <span style={s.legendItem}><span style={{ ...s.swatch, backgroundColor: "#7ed957" }} />ATL</span>
        <span style={s.legendItem}><span style={{ ...s.swatch, backgroundColor: "#ff6b6b" }} />TSB</span>
      </div>
    </div>
  );
}

export function ProgressPage() {
  const [entries, setEntries] = useState<ProgressEntry[]>([]);
  const [records, setRecords] = useState<PersonalRecord[]>([]);
  const [tl, setTl] = useState<TLPoint[]>([]);
  const [tlSum, setTlSum] = useState<TLResponse["summary"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addDate, setAddDate] = useState(todayIso());
  const [addWeight, setAddWeight] = useState("");
  const [addBodyFat, setAddBodyFat] = useState("");
  const [addM, setAddM] = useState(M);
  const [saving, setSaving] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  async function loadAll() {
    setLoading(true); setError(null);
    try {
      const [p, r, t] = await Promise.all([
        api.get<ProgressEntry[]>("/api/user/progress?limit=10"),
        api.get<PersonalRecord[]>("/api/user/personal-records"),
        api.get<TLResponse>("/api/user/training-load?days=30"),
      ]);
      setEntries(p || []); setRecords(r || []);
      setTl(t?.data || []); setTlSum(t?.summary || null);
      setPhotoUrl((p || []).find((e) => e.photo_url)?.photo_url ?? null);
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Failed to load progress"); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, r, t] = await Promise.all([
          api.get<ProgressEntry[]>("/api/user/progress?limit=10"),
          api.get<PersonalRecord[]>("/api/user/personal-records"),
          api.get<TLResponse>("/api/user/training-load?days=30"),
        ]);
        if (cancelled) return;
        setEntries(p || []); setRecords(r || []);
        setTl(t?.data || []); setTlSum(t?.summary || null);
        setPhotoUrl((p || []).find((e) => e.photo_url)?.photo_url ?? null);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load progress");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const latest = useMemo(() => entries[0] ?? null, [entries]);

  async function submitEntry(e: React.FormEvent) {
    e.preventDefault();
    if (!addDate) return;
    setSaving(true);
    try {
      const meas: Record<string, number> = {};
      for (const [k, v] of Object.entries(addM)) {
        const n = parseFloat(v);
        if (v.trim() !== "" && !Number.isNaN(n)) meas[k] = n;
      }
      await api.post<ProgressEntry>("/api/user/progress", {
        date: addDate,
        weight: addWeight.trim() === "" ? undefined : parseFloat(addWeight),
        body_fat: addBodyFat.trim() === "" ? undefined : parseFloat(addBodyFat),
        measurements: Object.keys(meas).length > 0 ? meas : undefined,
        photo_url: photoUrl ?? undefined,
      });
      setShowAdd(false);
      setAddDate(todayIso()); setAddWeight(""); setAddBodyFat(""); setAddM(M);
      await loadAll();
    } catch (err: unknown) { setError(err instanceof Error ? err.message : "Failed to save entry"); }
    finally { setSaving(false); }
  }

  async function handlePhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("image", file);
      const res = await api.upload<{ photo_url: string }>("/api/user/progress/photo", fd);
      setPhotoUrl(res.photo_url);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : "Photo upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  }

  return (
    <div style={s.page}>
      <h1 style={s.title}>Progress</h1>
      {error && <div style={s.errBanner}>{error}</div>}

      <div style={s.summaryCard}>
        <div style={s.summaryMain}>
          <div style={s.stat}><div style={s.statL}>Weight</div><div style={s.statV}>{latest?.weight != null ? `${latest.weight} kg` : "—"}</div></div>
          <div style={s.stat}><div style={s.statL}>Body Fat</div><div style={s.statV}>{latest?.body_fat != null ? `${latest.body_fat}%` : "—"}</div></div>
          <div style={s.stat}><div style={s.statL}>CTL</div><div style={s.statV}>{tlSum ? Math.round(tlSum.current_ctl) : "—"}</div></div>
          <div style={s.stat}><div style={s.statL}>TSB</div><div style={{ ...s.statV, color: tlSum == null ? "#fff" : tlSum.current_tsb >= 0 ? "#7ed957" : "#ff6b6b" }}>{tlSum ? Math.round(tlSum.current_tsb) : "—"}</div></div>
        </div>
        <div style={s.photoBlock}>
          {photoUrl
            ? <img src={photoUrl} alt="Progress" style={s.thumb} />
            : <div style={s.photoPh}>No photo</div>}
          <label style={s.uploadBtn}>
            {uploading ? "Uploading..." : "Upload Photo"}
            <input type="file" accept="image/*" onChange={handlePhoto} style={{ display: "none" }} disabled={uploading} />
          </label>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardHdr}><h2 style={s.cardTitle}>Training Load (30d)</h2></div>
        {loading ? <div style={s.muted}>Loading training data...</div> : <TrainingLoadChart data={tl} />}
      </div>

      <div style={s.card}>
        <div style={s.cardHdr}>
          <h2 style={s.cardTitle}>Recent Entries</h2>
          <button type="button" onClick={() => setShowAdd(true)} style={s.addBtn}>+ Add Entry</button>
        </div>
        {loading ? <div style={s.muted}>Loading entries...</div>
          : entries.length === 0 ? <div style={s.muted}>No entries yet. Add your first measurement to start tracking.</div>
          : <ul style={s.list}>
              {entries.map((e) => (
                <li key={e.id} style={s.row}>
                  <div style={s.rowDate}>{fmtDate(e.date)}</div>
                  <div style={s.rowM}>{e.weight != null ? `${e.weight} kg` : "—"}</div>
                  <div style={s.rowM}>{e.body_fat != null ? `${e.body_fat}%` : "—"}</div>
                </li>
              ))}
            </ul>}
      </div>

      <div style={s.card}>
        <div style={s.cardHdr}><h2 style={s.cardTitle}>Personal Records</h2></div>
        {loading ? <div style={s.muted}>Loading personal records...</div>
          : records.length === 0 ? <div style={s.muted}>No personal records yet. Train and import activities to set PRs.</div>
          : <ul style={s.list}>
              {records.map((r) => (
                <li key={r.id} style={s.row}>
                  <div style={s.rowM}>{ML[r.metric] ?? r.metric}</div>
                  <div style={s.rowM}>{r.value} {r.unit}</div>
                  <div style={s.rowDate}>{new Date(r.achieved_at * 1000).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>}
      </div>

      {showAdd && (
        <div style={s.backdrop} onClick={() => !saving && setShowAdd(false)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHdr}>
              <h3 style={s.modalTitle}>Add Progress Entry</h3>
              <button type="button" onClick={() => setShowAdd(false)} style={s.close} disabled={saving} aria-label="Close">×</button>
            </div>
            <form onSubmit={submitEntry} style={s.form}>
              <label style={s.field}><span style={s.fieldL}>Date</span>
                <input type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} style={s.input} required />
              </label>
              <label style={s.field}><span style={s.fieldL}>Weight (kg)</span>
                <input type="number" step="0.1" value={addWeight} onChange={(e) => setAddWeight(e.target.value)} style={s.input} placeholder="e.g. 72.5" />
              </label>
              <label style={s.field}><span style={s.fieldL}>Body Fat (%)</span>
                <input type="number" step="0.1" value={addBodyFat} onChange={(e) => setAddBodyFat(e.target.value)} style={s.input} placeholder="e.g. 15.0" />
              </label>
              <div style={s.mHdr}>Measurements (cm, optional)</div>
              <div style={s.mGrid}>
                {(Object.keys(M) as (keyof typeof M)[]).map((k) => (
                  <label key={k} style={s.field}>
                    <span style={s.fieldL}>{k}</span>
                    <input type="number" step="0.1" value={addM[k]} onChange={(e) => setAddM((m) => ({ ...m, [k]: e.target.value }))} style={s.input} />
                  </label>
                ))}
              </div>
              <div style={s.actions}>
                <button type="button" onClick={() => setShowAdd(false)} style={s.secBtn} disabled={saving}>Cancel</button>
                <button type="submit" style={s.priBtn} disabled={saving}>{saving ? "Saving..." : "Save Entry"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { fontFamily: "Inter, system-ui, Arial", color: "#fff", padding: 16, minHeight: "calc(100vh - 56px - 60px)", display: "flex", flexDirection: "column", gap: 16 },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  errBanner: { padding: "8px 12px", backgroundColor: "#3a1a1a", color: "#ff9a9a", borderRadius: 6, fontSize: 13 },
  card: { backgroundColor: "#1a1a1a", borderRadius: 12, padding: 16, border: "1px solid #2a2a2a" },
  cardHdr: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  cardTitle: { fontSize: 16, fontWeight: 600, margin: 0, color: "#fff" },
  summaryCard: { backgroundColor: "#1a1a1a", borderRadius: 12, padding: 16, border: "1px solid #2a2a2a", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" },
  summaryMain: { display: "flex", gap: 24, flex: 1, flexWrap: "wrap" },
  stat: { minWidth: 70 },
  statL: { fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  statV: { fontSize: 22, fontWeight: 700, marginTop: 4 },
  photoBlock: { display: "flex", flexDirection: "column", gap: 8, alignItems: "center" },
  thumb: { width: 80, height: 80, borderRadius: 8, objectFit: "cover", border: "1px solid #333" },
  photoPh: { width: 80, height: 80, borderRadius: 8, backgroundColor: "#2a2a2a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#666" },
  uploadBtn: { padding: "6px 12px", borderRadius: 6, backgroundColor: "#2a2a2a", color: "#fff", fontSize: 12, cursor: "pointer", border: "1px solid #444", textAlign: "center" },
  addBtn: { padding: "6px 12px", borderRadius: 6, border: "none", backgroundColor: "#ff6b35", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  list: { listStyle: "none", margin: 0, padding: 0 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #222", gap: 12 },
  rowDate: { color: "#aaa", fontSize: 13, flex: 1 },
  rowM: { color: "#fff", fontSize: 14, fontWeight: 500, flex: 1, textAlign: "right" },
  chartWrap: { display: "flex", flexDirection: "column", gap: 8 },
  chartSvg: { width: "100%", height: "auto", display: "block" },
  chartEmpty: { padding: 32, textAlign: "center", color: "#888", fontSize: 14, backgroundColor: "#0f0f0f", borderRadius: 8 },
  legend: { display: "flex", gap: 16, justifyContent: "center", fontSize: 12, color: "#ccc" },
  legendItem: { display: "flex", alignItems: "center", gap: 6 },
  swatch: { width: 12, height: 12, borderRadius: 2, display: "inline-block" },
  muted: { padding: 16, textAlign: "center", color: "#888", fontSize: 14 },
  backdrop: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 },
  modal: { backgroundColor: "#1a1a1a", borderRadius: 12, border: "1px solid #2a2a2a", width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column" },
  modalHdr: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid #2a2a2a" },
  modalTitle: { fontSize: 16, fontWeight: 600, margin: 0 },
  close: { background: "transparent", border: "none", color: "#888", fontSize: 24, cursor: "pointer", lineHeight: 1, padding: 0 },
  form: { display: "flex", flexDirection: "column", gap: 12, padding: 16 },
  field: { display: "flex", flexDirection: "column", gap: 4, flex: 1 },
  fieldL: { fontSize: 12, color: "#aaa" },
  input: { padding: "8px 10px", borderRadius: 6, border: "1px solid #444", backgroundColor: "#0f0f0f", color: "#fff", fontSize: 14, outline: "none" },
  mHdr: { fontSize: 12, color: "#aaa", marginTop: 4 },
  mGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  actions: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 },
  priBtn: { padding: "10px 18px", borderRadius: 8, border: "none", backgroundColor: "#ff6b35", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: 14 },
  secBtn: { padding: "10px 18px", borderRadius: 8, border: "1px solid #444", backgroundColor: "transparent", color: "#fff", cursor: "pointer", fontSize: 14 },
};
